/**
 * Money / number formatting.
 *
 * **Rule:** every monetary value rendered in the UI goes through one of
 * these helpers. Direct `.toFixed(2)` is forbidden by lint — the only
 * authoritative place to decide rounding mode + locale is here.
 */

const TWO_DP = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function toNumber(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatUsd(v: number | string | null | undefined): string {
  return `$${TWO_DP.format(toNumber(v))}`;
}

export function formatUsdSigned(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === 0) return '$0.00';
  return `${n > 0 ? '+' : '−'}$${TWO_DP.format(Math.abs(n))}`;
}

export function formatNumber(v: number | string | null | undefined): string {
  return TWO_DP.format(toNumber(v));
}

export function formatPercent(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === 0) return '0.00%';
  if (n < 0) return `−${TWO_DP.format(Math.abs(n))}%`;
  return `${TWO_DP.format(n)}%`;
}

/**
 * 2-dp formatter for non-USD numerics (probabilities, sizes,
 * pre-formatted decimals coming from the backend that arrive with
 * trailing zeros like "0.5500000000").
 */
export function formatDecimal2(v: number | string | null | undefined): string {
  return TWO_DP.format(toNumber(v));
}

/**
 * Adaptive-precision USD magnitude — `$|v|` with enough decimals to
 * stay legible across mixed market scales (BTC dollars, ETH dollars,
 * SOL cents). Used for the price-to-beat delta indicator where a flat
 * `toFixed(0)` collapsed cent-level moves on smaller assets to "$0".
 *
 *   |Δ| ≥ 100  → 0 dp  ("23")
 *   |Δ| ≥ 10   → 1 dp  ("5.4")
 *   otherwise  → 2 dp  ("0.42")
 *
 * The caller owns the sign — this returns the unsigned magnitude
 * (▲ / ▼ markers and tint colour are decided contextually upstream).
 */
export function formatUsdAbsAdaptive(v: number): string {
  const abs = Math.abs(v);
  const maxDigits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `$${abs.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  })}`;
}

/**
 * Largest 2-dp value ≤ `n` — i.e. floor to cents, never round up. Use
 * this (not `.toFixed(2)`, which rounds half-up) anywhere a "max"
 * amount is both *displayed* and *fed back into a submit guard*: a
 * value like 11.3898 must surface as 11.38, not 11.39, or the MAX
 * button fills an amount the guard then rejects. The tiny epsilon
 * absorbs float noise (e.g. 0.29 stored as 0.289999…) without ever
 * pushing a genuinely-fractional value up a full cent.
 */
export function floorToCents(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n * 100 + 1e-6) / 100;
}

/**
 * Format an ISO timestamp as a short clock time in the viewer's locale +
 * local timezone ("19:00" for ru-RU, "7:00 PM" for en-US), adding a
 * short date when it isn't today. Used for the bot auto-resume time.
 * Returns '' for a missing/unparseable value.
 */
export function formatResumeTime(
  iso: string | null | undefined,
  locale: string,
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return time;
  const date = d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  return `${date}, ${time}`;
}

/**
 * Detect a backend market label that's actually a raw 0x-prefixed
 * hex hash (Polymarket condition_id). Such values must NEVER be
 * shown to the user — they're internal identifiers.
 */
export function isRawMarketHash(market: string | null | undefined): boolean {
  if (!market) return false;
  return /^0x[0-9a-fA-F]{8,}$/.test(market.trim());
}

export function shortAddress(
  addr: string | null | undefined,
  head = 6,
  tail = 4,
): string {
  if (!addr) return '';
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}
